<template>
  <div id="guie" class="landing-page">
    <section class="landing-hero">
      <div class="landing-hero-inner">
        <div class="landing-hero-content">
          <img
            src="@/assets/logo_Mytho.png"
            alt="Mytho - El relato que nos orienta"
            class="landing-hero-logo"
          />
          <h1 class="landing-hero-title">
            Cultura <span>Organizacional</span>
          </h1>
          <p class="landing-hero-description">
            La cultura organizacional es el conjunto de valores, creencias y
            normas que guían la forma en que las personas trabajan y se
            relacionan dentro de una empresa. Es como la "personalidad" de la
            organización y afecta todo lo que sucede en su interior.
          </p>
          <div class="landing-hero-actions">
            <a
              href="#resultados"
              class="btn btn-primary"
              v-on:click="continueToTable"
            >
              Continuar a los resultados
              <svg
                class="btn-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </a>
            <a :href="urlGuia" target="_blank" class="btn btn-outline">
              <svg
                class="btn-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Descargar Guía
            </a>
          </div>
        </div>
        <div class="landing-hero-visual">
          <div class="landing-hero-image-wrapper">
            <img
              src="@/assets/cultura_modelo_mytho_.png"
              alt="Modelo de Cultura Organizacional"
              class="landing-hero-image"
            />
          </div>
        </div>
      </div>
    </section>
    <section id="importancia" class="landing-section">
      <div class="landing-section-inner">
        <div class="landing-section-header">
          <span class="landing-section-subtitle">Fundamentos</span>
          <h2 class="landing-section-title">
            ¿Por qué es clave para el éxito?
          </h2>
          <p class="landing-section-description">
            Según Cameron y Quinn (2011), la cultura organizacional está
            compuesta por valores asumidos, expectativas y definiciones
            compartidas que caracterizan a la empresa y a sus miembros.
          </p>
        </div>

        <div class="landing-importance-grid">
          <div class="landing-importance-item">
            <div class="landing-importance-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p>
              Define <strong>valores y creencias</strong> que guían la
              motivación y el comportamiento de las personas.
            </p>
          </div>
          <div class="landing-importance-item">
            <div class="landing-importance-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p>
              Da forma a las <strong>prácticas y procedimientos</strong> del día
              a día.
            </p>
          </div>
          <div class="landing-importance-item">
            <div class="landing-importance-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p>
              Influye en cómo se <strong>resuelven los conflictos</strong>,
              afectando la armonía y eficiencia del equipo.
            </p>
          </div>
          <div class="landing-importance-item">
            <div class="landing-importance-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p>
              Genera un <strong>sentido de propósito</strong>, alineando a todos
              hacia objetivos comunes.
            </p>
          </div>
          <div class="landing-importance-item">
            <div class="landing-importance-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p>
              Afecta la <strong>toma de decisiones estratégicas</strong>, como
              la gestión de talento.
            </p>
          </div>
          <div class="landing-importance-item">
            <div class="landing-importance-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p>
              Facilita la <strong>adaptación al cambio</strong>, determinando la
              capacidad de evolucionar.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="tipos" class="landing-section landing-section-alt">
      <div class="landing-section-inner">
        <div class="landing-section-header">
          <span class="landing-section-subtitle"
            >Competing Values Framework</span
          >
          <h2 class="landing-section-title">
            Tipos de Cultura Organizacional
          </h2>
          <p class="landing-section-description">
            Nos basamos en el modelo "Competing Values Framework" de Cameron
            &amp; Quinn (2011), que identifica cuatro tipos de cultura
            organizacional.
          </p>
        </div>

        <div class="landing-culture-grid">
          <div class="landing-culture-card landing-culture-colaboracion">
            <div class="landing-culture-header">
              <div class="landing-culture-icon">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="landing-culture-title">Colaboración</h3>
                <span class="landing-culture-subtitle">Clan</span>
              </div>
            </div>
            <p class="landing-culture-tagline">Comunidad y Compromiso</p>
            <p class="landing-culture-description">
              Aquí, la empresa se siente como una gran familia. Se fomenta un
              ambiente cercano y participativo, donde se valora el trabajo en
              equipo y el sentido de pertenencia.
            </p>
            <ul class="landing-culture-list">
              <li>Relaciones cercanas y trabajo colaborativo</li>
              <li>Enfoque en la participación</li>
              <li>Liderazgo basado en desarrollo y formación</li>
              <li>Ambiente laboral amigable</li>
              <li>Equilibrio vida laboral y personal</li>
            </ul>
          </div>

          <div class="landing-culture-card landing-culture-creacion">
            <div class="landing-culture-header">
              <div class="landing-culture-icon">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="landing-culture-title">Creación</h3>
                <span class="landing-culture-subtitle">Adhocrática</span>
              </div>
            </div>
            <p class="landing-culture-tagline">
              Innovación, Flexibilidad y Creatividad
            </p>
            <p class="landing-culture-description">
              Si hay algo que define a este tipo de cultura, es la búsqueda
              constante de nuevas ideas. Aquí, la estructura es más flexible,
              los equipos son dinámicos y la experimentación es parte del día a
              día.
            </p>
            <ul class="landing-culture-list">
              <li>Trabajo basado en proyectos</li>
              <li>Alta autonomía y descentralización</li>
              <li>Énfasis en creatividad y disrupción</li>
              <li>Liderazgo visionario e inspirador</li>
              <li>Riesgos calculados y exploración</li>
            </ul>
          </div>

          <div class="landing-culture-card landing-culture-control">
            <div class="landing-culture-header">
              <div class="landing-culture-icon">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="landing-culture-title">Control</h3>
                <span class="landing-culture-subtitle">Jerárquica</span>
              </div>
            </div>
            <p class="landing-culture-tagline">Orden, Control y Estructura</p>
            <p class="landing-culture-description">
              Si hay algo que define este tipo de cultura, es la estabilidad y
              la eficiencia. Se basa en una estructura clara, donde las reglas,
              los procedimientos y la jerarquía juegan un papel fundamental.
            </p>
            <ul class="landing-culture-list">
              <li>Normas y procedimientos bien definidos</li>
              <li>Líneas de autoridad claras</li>
              <li>Énfasis en la formalidad</li>
              <li>Monitoreo y control constantes</li>
              <li>Liderazgo basado en organización y coordinación</li>
            </ul>
          </div>

          <div class="landing-culture-card landing-culture-competencia">
            <div class="landing-culture-header">
              <div class="landing-culture-icon">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="landing-culture-title">Competencia</h3>
                <span class="landing-culture-subtitle">Mercado</span>
              </div>
            </div>
            <p class="landing-culture-tagline">Resultados y Rentabilidad</p>
            <p class="landing-culture-description">
              En esta cultura, el enfoque está en el desempeño y la
              competitividad. Más que en reglas internas, la empresa se guía por
              el comportamiento del mercado y la necesidad de adaptarse a él
              para crecer.
            </p>
            <ul class="landing-culture-list">
              <li>Orientación a resultados</li>
              <li>Enfoque externo hacia clientes y mercado</li>
              <li>Alta competitividad</li>
              <li>Liderazgo enfocado en productividad</li>
              <li>Decisiones estratégicas basadas en tendencias</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section id="modelo" class="landing-section">
      <div class="landing-section-inner">
        <div class="landing-section-header">
          <span class="landing-section-subtitle">Modelo Cultura</span>
          <h2 class="landing-section-title">
            Cuatro dimensiones de la cultura
          </h2>
          <p class="landing-section-description">
            Nuestro modelo analiza tu organización a través de cuatro cuadrantes
            que representan diferentes enfoques culturales, equilibrando
            flexibilidad con consistencia y foco interno con externo.
          </p>
        </div>

        <div class="landing-modelo-grid">
          <div class="landing-modelo-card colaboracion">
            <div class="landing-modelo-icon">
              <svg
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 class="landing-modelo-title">Colaboración</h3>
            <p class="landing-modelo-text">
              <strong>Somos buenos porque:</strong> desarrollamos y damos
              participación.
            </p>
            <p class="landing-modelo-text">
              <strong>Nos guía:</strong> desarrollo, comunicación, compromiso.
            </p>
            <p class="landing-modelo-text">
              <strong>Líderes:</strong> facilitadores, mentores, coequiperos.
            </p>
          </div>

          <div class="landing-modelo-card creacion">
            <div class="landing-modelo-icon">
              <svg
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h3 class="landing-modelo-title">Creación</h3>
            <p class="landing-modelo-text">
              <strong>Somos buenos porque:</strong> innovamos y tenemos visión.
            </p>
            <p class="landing-modelo-text">
              <strong>Nos guía:</strong> transformación, agilidad, flexibilidad.
            </p>
            <p class="landing-modelo-text">
              <strong>Líderes:</strong> emprendedores, visionarios.
            </p>
          </div>

          <div class="landing-modelo-card control">
            <div class="landing-modelo-icon">
              <svg
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h3 class="landing-modelo-title">Control</h3>
            <p class="landing-modelo-text">
              <strong>Somos buenos porque:</strong> tenemos todo estandarizado y
              controlado.
            </p>
            <p class="landing-modelo-text">
              <strong>Nos guía:</strong> eficiencia, cronogramas, control,
              uniformidad.
            </p>
            <p class="landing-modelo-text">
              <strong>Líderes:</strong> coordinadores, organizadores, monitores.
            </p>
          </div>

          <div class="landing-modelo-card competencia">
            <div class="landing-modelo-icon">
              <svg
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h3 class="landing-modelo-title">Competencia</h3>
            <p class="landing-modelo-text">
              <strong>Somos buenos porque:</strong> ponemos foco en el cliente.
            </p>
            <p class="landing-modelo-text">
              <strong>Nos guía:</strong> metas, mercado, competencia.
            </p>
            <p class="landing-modelo-text">
              <strong>Líderes:</strong> estrictos, orientados a metas.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="diagnostico" class="landing-cta">
      <div class="landing-cta-inner">
        <h2 class="landing-cta-title">
          ¿Listo para conocer la cultura de tu equipo?
        </h2>

        <a
          href="#resultados"
          class="btn btn-primary"
          v-on:click="continueToTable"
        >
          Continuar a los resultados
          <svg
            class="btn-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </a>
      </div>
    </section>

    <footer class="landing-footer">
      <div class="landing-footer-inner">
        <img
          src="@/assets/logo_Mytho.png"
          alt="Mytho"
          class="landing-footer-logo"
        />
        <p class="landing-footer-text">
          © 2026 Mytho. El relato que nos orienta.
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapState } from "vuex";
import archivoPDF from "@/assets/files/guia_interpretacion_cultura_2026.pdf";

export default {
  data() {
    return {
      urlGuia: archivoPDF,
    };
  },
  computed: {
    ...mapState(["empresa", "idtoken", "isOculto"]),
  },
  methods: {
    continueToTable() {
      this.$emit("continue");
    },
    scrollTop() {
      // Wait for the tab fade transition to finish (0.4s + 0.2s delay) so the
      // outgoing tab is gone and #guie sits at its final position before we
      // scroll — otherwise we overshoot to the next section. Tune if needed.
      setTimeout(() => {
        const el = document.getElementById("guie");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 650);
    },
  },
  mounted() {
    this.scrollTop();
  },
};
</script>

<style scoped lang="scss">
/* ===================================
   MYTHO LANDING - CSS PURO
   Paleta: Neutros + Teal del logo
   =================================== */

/* Variables CSS */
.landing-page {
  --color-primary: #00b4c5;
  --color-primary-dark: #009aa8;
  --color-primary-light: #e0f7fa;
  --color-text: #1a1a2e;
  --color-text-light: #4a4a5a;
  --color-text-muted: #7a7a8a;
  --color-bg: #ffffff;
  --color-bg-alt: #f8f9fc;
  --color-bg-dark: #1a1a2e;
  --color-border: #e5e7eb;
  --font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07);
  --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 40px rgba(0, 0, 0, 0.12);
  --radius: 12px;
  --radius-lg: 20px;
  --transition: all 0.3s ease;
}

/* Reset básico */
.landing-page * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.landing-page {
  font-family: var(--font-family);
  color: var(--color-text);
  line-height: 1.6;
  background-color: var(--color-bg);
}

/* ===================================
   HERO SECTION
   =================================== */
.landing-hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 30px 2rem;
  background: linear-gradient(
    135deg,
    var(--color-bg) 0%,
    var(--color-bg-alt) 100%
  );
  position: relative;
  overflow: hidden;
}

.landing-hero::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 70%;
  height: 140%;
  background: radial-gradient(
    ellipse,
    rgba(0, 180, 197, 0.06) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.landing-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 1.5fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
}

.landing-hero-inner-centered {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
  width: 100%;
}

.landing-hero-content {
  max-width: 540px;
}

.landing-hero-inner-centered .landing-hero-content {
  max-width: 100%;
}

.landing-hero-inner-centered .landing-hero-logo {
  margin-left: auto;
  margin-right: auto;
}

.landing-hero-inner-centered .landing-hero-actions {
  justify-content: center;
}

.landing-hero-logo {
  height: 70px;
  width: auto;
  margin-bottom: 2rem;
}

.landing-hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.15;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
}

.landing-hero-title span {
  color: var(--color-primary);
}

.landing-hero-description {
  font-size: 1.15rem;
  color: var(--color-text-light);
  line-height: 1.7;
  margin-bottom: 2rem;
}

.landing-hero-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.landing-hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.landing-hero-image-wrapper {
  position: relative;
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--color-border);
}

.landing-hero-image-wrapper::before {
  content: "";
  position: absolute;
  top: -10px;
  right: -10px;
  bottom: -10px;
  left: -10px;
  background: linear-gradient(
    135deg,
    rgba(0, 180, 197, 0.1) 0%,
    transparent 50%
  );
  border-radius: calc(var(--radius-lg) + 10px);
  z-index: -1;
}

.landing-hero-image {
  width: 100%;
  max-width: 640px;
  height: auto;
  border-radius: var(--radius);
  display: block;
}

/* ===================================
   BOTONES
   =================================== */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: var(--radius);
  border: none;
  cursor: pointer;
  transition: var(--transition);
  text-decoration: none;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.btn-outline {
  background: transparent;
  color: var(--color-text);
  border: 2px solid var(--color-border);
}

.btn-outline:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.btn-icon {
  width: 20px;
  height: 20px;
}

/* ===================================
   SECCIONES
   =================================== */
.landing-section {
  padding: 100px 2rem;
}

.landing-section-alt {
  background: var(--color-bg-alt);
}

.landing-section-dark {
  background: var(--color-bg-dark);
  color: white;
}

.landing-section-inner {
  max-width: 1200px;
  margin: 0 auto;
}

.landing-section-header {
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
}

.landing-section-subtitle {
  display: inline-block;
  color: var(--color-primary);
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 1rem;
}

.landing-section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
  letter-spacing: -0.01em;
}

.landing-section-dark .landing-section-title {
  color: white;
}

.landing-section-description {
  font-size: 1.1rem;
  color: var(--color-text-light);
  line-height: 1.7;
}

.landing-section-dark .landing-section-description {
  color: rgba(255, 255, 255, 0.7);
}

/* ===================================
   IMPORTANCIA GRID
   =================================== */
.landing-importance-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.landing-importance-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: white;
  padding: 1.5rem;
  border-radius: var(--radius);
  border: 1px solid var(--color-border);
  transition: var(--transition);
}

.landing-importance-item:hover {
  box-shadow: var(--shadow-md);
  border-color: transparent;
}

.landing-importance-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: var(--color-primary-light);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
}

.landing-importance-item p {
  font-size: 0.95rem;
  color: var(--color-text-light);
  line-height: 1.6;
}

.landing-importance-item strong {
  color: var(--color-text);
}

/* ===================================
   TIPOS DE CULTURA GRID
   =================================== */
.landing-culture-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.landing-culture-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  border: 1px solid var(--color-border);
  transition: var(--transition);
}

.landing-culture-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: transparent;
}

.landing-culture-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.landing-culture-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
}

.landing-culture-control .landing-culture-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.landing-culture-competencia .landing-culture-icon {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.landing-culture-colaboracion .landing-culture-icon {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
}

.landing-culture-creacion .landing-culture-icon {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.landing-culture-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.landing-culture-subtitle {
  font-size: 0.85rem;
  color: var(--color-text-muted);
  font-weight: 500;
}

.landing-culture-tagline {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 0.75rem;
}

.landing-culture-description {
  font-size: 0.95rem;
  color: var(--color-text-light);
  line-height: 1.6;
  margin-bottom: 1.25rem;
}

.landing-culture-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.landing-culture-list li {
  position: relative;
  padding-left: 1.25rem;
  font-size: 0.9rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.landing-culture-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 6px;
  height: 6px;
  background: var(--color-primary);
  border-radius: 50%;
}

/* ===================================
   MODELO CULTURA GRID 2x2
   =================================== */
.landing-modelo-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
}

.landing-modelo-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  border: 1px solid var(--color-border);
  transition: var(--transition);
}

.landing-modelo-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: transparent;
}

.landing-modelo-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.landing-modelo-card.colaboracion .landing-modelo-icon {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
}

.landing-modelo-card.creacion .landing-modelo-icon {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.landing-modelo-card.control .landing-modelo-icon {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.landing-modelo-card.competencia .landing-modelo-icon {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.landing-modelo-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1rem;
}

.landing-modelo-text {
  font-size: 0.95rem;
  color: var(--color-text-muted);
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.landing-modelo-text:last-child {
  margin-bottom: 0;
}

.landing-modelo-text strong {
  color: var(--color-text-light);
}

/* ===================================
   GRID DE CARACTERÍSTICAS
   =================================== */
.landing-features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}

.landing-feature-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  border: 1px solid var(--color-border);
  transition: var(--transition);
}

.landing-feature-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: transparent;
}

.landing-feature-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  font-size: 1.5rem;
}

.landing-feature-icon.colaboracion {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
}

.landing-feature-icon.creacion {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.landing-feature-icon.control {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.landing-feature-icon.competencia {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.landing-feature-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
}

.landing-feature-description {
  font-size: 0.95rem;
  color: var(--color-text-muted);
  line-height: 1.6;
}

/* ===================================
   SIGUIENTE PASO
   =================================== */
.landing-next-steps {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.landing-next-content {
  max-width: 500px;
}

.landing-section-dark .landing-section-subtitle {
  color: var(--color-primary);
}

.landing-next-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1.5rem;
  line-height: 1.3;
}

.landing-next-description {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin-bottom: 1rem;
}

.landing-next-features {
  background: rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg);
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.landing-next-features-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 1.5rem;
}

.landing-next-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.landing-next-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.landing-next-list li:last-child {
  margin-bottom: 0;
}

.landing-next-list svg {
  flex-shrink: 0;
  color: var(--color-primary);
  margin-top: 2px;
}

/* ===================================
   CTA SECTION
   =================================== */
.landing-cta {
  text-align: center;
  padding: 80px 2rem;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
}

.landing-cta-inner {
  max-width: 600px;
  margin: 0 auto;
}

.landing-cta-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
}

.landing-cta-description {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
}

.landing-cta .btn-primary {
  background: white;
  color: var(--color-primary-dark);
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
}

.landing-cta .btn-primary:hover {
  background: rgba(255, 255, 255, 0.95);
  transform: translateY(-2px);
}

/* ===================================
   FOOTER
   =================================== */
.landing-footer {
  background: var(--color-bg-dark);
  padding: 3rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.landing-footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.landing-footer-logo {
  height: 40px;
  width: auto;
  opacity: 0.9;
}

.landing-footer-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

/* ===================================
   RESPONSIVE
   =================================== */
@media (max-width: 1024px) {
  .landing-hero-inner {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 3rem;
  }

  .landing-hero-content {
    max-width: 100%;
  }

  .landing-hero-logo {
    margin-left: auto;
    margin-right: auto;
  }

  .landing-hero-actions {
    justify-content: center;
  }

  .landing-hero-title {
    font-size: 2.75rem;
  }

  .landing-hero-image-wrapper {
    padding: 1.5rem;
  }

  .landing-hero-image {
    max-width: 100%;
  }

  .landing-features-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .landing-importance-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .landing-culture-grid {
    grid-template-columns: 1fr;
  }

  .landing-modelo-grid {
    grid-template-columns: 1fr;
  }

  .landing-next-steps {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .landing-hero {
    padding: 40px 1rem;
  }

  .landing-hero-logo {
    height: 60px;
  }

  .landing-hero-title {
    font-size: 2.25rem;
  }

  .landing-hero-description {
    font-size: 1rem;
  }

  .landing-hero-image-container {
    padding: 1rem;
  }

  .landing-section {
    padding: 60px 1rem;
  }

  .landing-section-title {
    font-size: 1.75rem;
  }

  .landing-features-grid {
    grid-template-columns: 1fr;
  }

  .landing-importance-grid {
    grid-template-columns: 1fr;
  }

  .landing-modelo-container {
    padding: 1rem;
  }

  .landing-footer-inner {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .landing-cta-title {
    font-size: 1.5rem;
  }

  .landing-next-title {
    font-size: 1.5rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/results/GuiaInterpretacion.vue